/**
 * printDeliverySlip.js
 * ────────────────────────────────────────────────────────────────
 * ใช้สำหรับสั่งพิมพ์ใบส่งเอกสาร (Delivery Acknowledgement Slip)
 * เรียกใช้จากทั้ง admin-request-detail และ user-request-detail
 *
 * Flow:
 *   1. Frontend ส่ง request ไปที่ backend พร้อม JWT token
 *   2. Backend query ข้อมูล request + generate PDF
 *   3. Frontend รับ PDF blob กลับมา → เปิดใน tab ใหม่ (Chrome PDF viewer)
 *
 * Backend endpoint: POST /generates/requests/{id_request}?type=pdf
 * ────────────────────────────────────────────────────────────────
 */

import config from '../../config.json';
import { getAccessToken } from '../../services/apiClient';

/**
 * printDeliverySlip — สั่งพิมพ์ใบส่งเอกสารจาก backend
 *
 * @param {object} reqValues    - F.values จาก Formik (ข้อมูล request หลัก)
 * @param {Array}  destinations - extra destinations (จุดหมายที่ 2, 3 ไม่รวม main)
 * @param {string} refNo        - Ref No. ของ request หลัก เช่น "2025100113"
 */
export const printDeliverySlip = async (reqValues, destinations, refNo) => {
    const requestId = reqValues.id;

    // ── guard: ต้องมี id ก่อนถึงจะเรียก API ได้ ──
    if (!requestId) {
        console.error('printDeliverySlip: ไม่มี request id');
        return;
    }

    try {
        // ── 1. ดึง JWT token จาก storage ──
        const token = await getAccessToken();

        // ── 2. เรียก backend ให้ generate PDF ──
        //    backend จะ query ข้อมูลจาก DB และ return PDF binary
        const url = `${config.API_URL}/files/generates/cover-pages?ids=${requestId}`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,  // ส่ง token เพื่อ authenticate
            },
        });

        // ── 3. ตรวจสอบ response status ──
        if (!response.ok) {
            console.error('printDeliverySlip: API error', response.status);
            return;
        }

        // ── 4. แปลง response เป็น Blob (binary PDF) ──
        const blob = await response.blob();

        // ── 5. สร้าง temporary URL จาก blob แล้วเปิดใน tab ใหม่ ──
        //    browser จะแสดง Chrome PDF viewer พร้อม thumbnail, zoom, print, download
        const blobUrl = URL.createObjectURL(blob);
        const tab = window.open(blobUrl, '_blank');

        // ── 6. revoke URL เพื่อ free memory ──
        //    รอให้ tab โหลดเสร็จก่อน หรือ timeout 60 วินาที
        if (tab) {
            tab.addEventListener('load', () => URL.revokeObjectURL(blobUrl));
        }
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

    } catch (err) {
        console.error('printDeliverySlip error:', err);
    }
};